import type { AccountListItem } from '../../lib/types/finance.js';

export interface CoaCreateLevel {
  readonly levelId: string;
  readonly sequence: number;
  readonly displayName: string;
  readonly status: string;
}

/** Invalid or ambiguous metadata must never turn into a guessed chart structure. */
export function activeCoaCreateLevels(response: unknown): readonly CoaCreateLevel[] | null {
  if (!response || typeof response !== 'object' || !('items' in response)) return null;
  if (!Array.isArray(response.items)) return null;
  const ids = new Set<string>();
  const sequences = new Set<number>();
  const levels: CoaCreateLevel[] = [];
  for (const item of response.items as unknown[]) {
    if (!item || typeof item !== 'object') return null;
    const level = item as Partial<CoaCreateLevel>;
    if (
      typeof level.levelId !== 'string' ||
      !level.levelId.trim() ||
      typeof level.sequence !== 'number' ||
      !Number.isSafeInteger(level.sequence) ||
      level.sequence < 1 ||
      typeof level.displayName !== 'string' ||
      !level.displayName.trim() ||
      typeof level.status !== 'string' ||
      ids.has(level.levelId) ||
      sequences.has(level.sequence)
    )
      return null;
    ids.add(level.levelId);
    sequences.add(level.sequence);
    if (level.status === 'active') levels.push(level as CoaCreateLevel);
  }
  return levels.length ? levels.sort((a, b) => a.sequence - b.sequence) : null;
}

export function coaParentIsEligible(
  parent: AccountListItem,
  levels: readonly CoaCreateLevel[],
  sequence: number,
): boolean {
  const level = levels.find((item) => item.levelId === parent.levelId);
  return (
    parent.status === 'active' &&
    !parent.isPostable &&
    level !== undefined &&
    level.sequence < sequence
  );
}

/** Selecting a parent uses its next configured active level, not tree depth or a fixed level 4. */
export function nextCoaCreateSequence(
  levels: readonly CoaCreateLevel[],
  parent?: AccountListItem,
): number | null {
  if (!parent) return levels[0]?.sequence ?? null;
  const parentLevel = levels.find((item) => item.levelId === parent.levelId);
  if (parent.status !== 'active' || parent.isPostable || !parentLevel) return null;
  return levels.find((item) => item.sequence > parentLevel.sequence)?.sequence ?? null;
}

export function coaLevelCanPost(
  levels: readonly CoaCreateLevel[],
  sequence: number | null,
): boolean {
  return sequence !== null && levels.at(-1)?.sequence === sequence;
}

export function coaCreateSelectionError(
  levels: readonly CoaCreateLevel[] | null,
  accounts: readonly AccountListItem[] | null,
  sequence: number | null,
  parentRef: string,
  isPostable: boolean,
): string | null {
  if (!levels || !accounts) return 'اطلاعات سطح‌ها و حساب‌ها آماده نیست؛ دوباره بارگذاری کنید.';
  if (!levels.some((level) => level.sequence === sequence))
    return 'یک سطح فعال از سطح‌های تعریف‌شده انتخاب کنید.';
  if (!parentRef) {
    if (sequence !== levels[0]?.sequence)
      return 'برای این سطح، یک حساب والد از سطحی پیش از سطح انتخاب‌شده انتخاب کنید.';
  } else {
    const parent = accounts.find((account) => account.code === parentRef);
    if (!parent || sequence === null || !coaParentIsEligible(parent, levels, sequence)) {
      return 'حساب والد باید فعال، غیرقابل ثبت سند و از سطحی پیش از سطح انتخاب‌شده باشد؛ والد را دوباره انتخاب کنید.';
    }
  }
  if (isPostable && !coaLevelCanPost(levels, sequence))
    return 'فقط آخرین سطح فعال می‌تواند قابل ثبت سند باشد.';
  return null;
}
